import { useCallback, useEffect, useMemo, useState } from 'react';
import { API, CourseUpdatesResponse } from '../api';
import useCourseVersioning from './useCourseVersioning';
import { deriveLearnerWhatsNew, LearnerWhatsNew } from '../components/course-versioning/learnerWhatsNew';

/**
 * Course versioning, learner side: the one-time "What's new in <program>"
 * pop-up. When the org flag is on, opening a program calls
 * GET programs/{grpId}/updates; the notice shows once when the live version is
 * newer than the learner's `lastSeenVersion` and the publish notified learners,
 * and closing records the version through POST updates/seen. A version shown
 * in this session is never shown again (lesson pages remount on every lesson;
 * the seen write is asynchronous).
 */
const shownThisSession = new Set<string>();

interface WhatsNewState {
  open: boolean;
  data: CourseUpdatesResponse | null;
  version: number;
}

const useProgramWhatsNew = (
  grpId: string | undefined,
  enabled = true,
  /** The program's lessons, id → title, in course order (for deriveLearnerWhatsNew). */
  lessonTitles?: Map<string, string>
) => {
  const versioningOn = useCourseVersioning();
  const [state, setState] = useState<WhatsNewState>({ open: false, data: null, version: 0 });

  useEffect(() => {
    if (!versioningOn || !enabled || !grpId) return undefined;
    let cancelled = false;
    API.getCourseUpdates(grpId)
      .then((res) => {
        const data = res?.data?.data;
        // A silent (Minor Fix) publish carries no notice.
        if (cancelled || !data || !(data.learnerWhatsNew || data.whatsNew)) return;
        const seen = data.lastSeenVersion ?? 0;
        if (data.currentVersion <= seen) return;
        const key = `${grpId}:${data.currentVersion}`;
        if (shownThisSession.has(key)) return;
        shownThisSession.add(key);
        setState({ open: true, data, version: data.currentVersion });
      })
      .catch(() => {
        /* VERSIONING_OFF is handled by the session switch; anything else simply shows no pop-up */
      });
    return () => {
      cancelled = true;
    };
  }, [versioningOn, enabled, grpId]);

  const learnerWhatsNew = useMemo<LearnerWhatsNew | null>(() => {
    if (!state.data) return null;
    return state.data.learnerWhatsNew ?? deriveLearnerWhatsNew(state.data, lessonTitles || new Map());
  }, [state.data, lessonTitles]);

  const dismiss = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
    if (grpId && state.version > 0) {
      API.markCourseUpdatesSeen(grpId, state.version).catch(() => undefined);
    }
  }, [grpId, state.version]);

  return { open: state.open, learnerWhatsNew, version: state.version, dismiss };
};

export default useProgramWhatsNew;
